// Iter 82.7 / Phase E2.3 — Employee Attendance Portal.
// Personal analytics dashboard: prominent Today's Status widget,
// MTD KPIs, last-12-months trend strip, reliability score + classification,
// pending regularizations, recent anomalies, shift assignment.
// Powered by /api/attendance/dashboard/employee.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Clock, Warning, ChartLineUp, IdentificationCard,
    CheckCircle, XCircle, FileText, Hourglass,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { formatDate, formatTime } from "../../lib/format";

const STATUS_TONE = {
    Present: "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Half Day": "bg-amber-100 text-amber-800 border-amber-200",
    Absent: "bg-rose-100 text-rose-800 border-rose-200",
    "On Leave": "bg-sky-100 text-sky-800 border-sky-200",
    Holiday: "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Weekly Off": "bg-slate-100 text-slate-700 border-slate-200",
};

const CLASS_TONE = {
    Excellent: "bg-emerald-50 border-emerald-300 text-emerald-900",
    Good: "bg-sky-50 border-sky-300 text-sky-900",
    "Needs Attention": "bg-amber-50 border-amber-300 text-amber-900",
    Critical: "bg-rose-50 border-rose-300 text-rose-900",
};

function pctBand(pct) {
    if (pct >= 95) return "bg-emerald-500";
    if (pct >= 85) return "bg-amber-500";
    return "bg-rose-500";
}

// Iter 102.2 · Attendance Timezone Cleanup — the previous local `formatTime`
// helper has been removed. All attendance timestamps are now rendered via
// the centralised `formatTime` / `formatTimeIST` in `/app/frontend/src/lib/
// format.js`, which forces the `Asia/Kolkata` timezone.

function KpiTile({ label, value, suffix = "", tone = "slate", testid, to, icon }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
    };
    const card = (
        <div className={`rounded-xl border p-4 ${map[tone]} ${to ? "hover:shadow transition" : ""}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
                {icon}
            </div>
            <div className="text-2xl font-bold mt-2">{value}{suffix && <span className="text-sm font-normal opacity-70 ml-1">{suffix}</span>}</div>
        </div>
    );
    return to ? <Link to={to}>{card}</Link> : card;
}

export default function AttendanceEmployeePortal() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/attendance/dashboard/employee");
            setData(payload);
            setErr(null);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                setErr("Your account is not linked to an employee record. Ask HR to link your account to access the attendance portal.");
            } else {
                toast.error(formatApiError(e, "Failed to load attendance portal"));
                setErr("Could not load portal");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="att-portal-loading">
                <PageHeader eyebrow="Attendance" title="My Attendance Portal" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }
    if (err) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="att-portal-error">
                <PageHeader eyebrow="Attendance" title="My Attendance Portal" />
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2">
                    <Warning size={18} className="mt-0.5" /> <span>{err}</span>
                </div>
            </div>
        );
    }
    if (!data) return null;

    const t = data.today_snapshot;
    const mtd = data.mtd || {};
    const classification = mtd.reliability_classification || "Critical";
    const score = mtd.reliability_score ?? 0;
    const months = data.last_12_months || [];
    const maxBarPct = 100; // attendance % is bounded
    const eid = data.employee?.id || "";

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="att-portal-page">
            <PageHeader
                eyebrow={`Hello, ${data.employee.full_name}`}
                title="My Attendance Portal"
                description={`${data.employee.department || ""} · ${data.employee.designation || ""} · ${data.shift?.name || "No shift assigned"}`}
                actions={(
                    <div className="flex gap-2">
                        <Link to="/attendance/me">
                            <Button variant="outline" size="sm" data-testid="att-portal-checkin-btn">
                                <Clock size={14} className="mr-1" /> Check-In / Out
                            </Button>
                        </Link>
                        <Link to="/regularization/new">
                            <Button size="sm" data-testid="att-portal-regularize-btn">
                                <FileText size={14} className="mr-1" /> Request Regularization
                            </Button>
                        </Link>
                    </div>
                )}
            />

            {/* Today's Status Widget — prominent */}
            <section className="mt-5 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="att-portal-today-widget">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Today · {formatDate(data.today)}</div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <Badge data-testid="att-portal-today-status" className={`text-sm px-3 py-1.5 border ${STATUS_TONE[t?.attendance_status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                                {t?.attendance_status || "Pending"}
                            </Badge>
                            {t?.is_late && <Badge data-testid="att-portal-today-late" className="bg-orange-100 text-orange-800 border border-orange-200 text-xs">Late by {t.late_minutes}m</Badge>}
                            {t?.is_early_departure && <Badge className="bg-orange-100 text-orange-800 border border-orange-200 text-xs">Early by {t.early_departure_minutes}m</Badge>}
                            {t?.is_regularized && <Badge className="bg-violet-100 text-violet-800 border border-violet-200 text-xs">Regularized</Badge>}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
                        <div data-testid="att-portal-today-checkin">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Check-In</div>
                            <div className="text-lg font-bold text-slate-900 mt-1">{formatTime(t?.check_in_time)}</div>
                        </div>
                        <div data-testid="att-portal-today-checkout">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Check-Out</div>
                            <div className="text-lg font-bold text-slate-900 mt-1">{formatTime(t?.check_out_time)}</div>
                        </div>
                        <div data-testid="att-portal-today-net">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Net Hours</div>
                            <div className="text-lg font-bold text-slate-900 mt-1">{t ? ((t.net_working_minutes || 0) / 60).toFixed(1) : "—"}h</div>
                        </div>
                        <div data-testid="att-portal-today-shift">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Shift</div>
                            <div className="text-lg font-bold text-slate-900 mt-1">{data.shift?.start_time || "—"}–{data.shift?.end_time || "—"}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reliability Score banner */}
            <section className={`mt-5 rounded-2xl border p-5 ${CLASS_TONE[classification]}`} data-testid="att-portal-reliability">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Attendance Reliability Score · MTD ({formatDate(mtd.period_from)} → {formatDate(mtd.period_to)})</div>
                        <div className="flex items-baseline gap-3 mt-2">
                            <div className="text-5xl font-bold" data-testid="att-portal-reliability-score">{score.toFixed(1)}</div>
                            <div className="text-sm opacity-80">/ 100</div>
                            <Badge data-testid="att-portal-reliability-class" className={`text-xs ${CLASS_TONE[classification]} border`}>{classification}</Badge>
                        </div>
                        <div className="text-xs opacity-80 mt-1">Risk factor · <span className="font-semibold" data-testid="att-portal-risk-factor">{mtd.primary_risk_factor || "—"}</span></div>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="h-3 bg-white/60 rounded-full overflow-hidden">
                            <div className="h-full bg-current opacity-70 transition-all" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
                        </div>
                    </div>
                </div>
            </section>

            {/* MTD KPIs with drill-down */}
            <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="att-portal-mtd-kpis">
                <KpiTile testid="kpi-attendance-pct" label="Attendance %" value={(mtd.attendance_pct ?? 0).toFixed(1)} suffix="%" tone="sky" icon={<ChartLineUp size={18} />} />
                <KpiTile testid="kpi-present-days" label="Present" value={mtd.present_days || 0} tone="emerald" icon={<CheckCircle size={18} />}
                    to={`/admin/attendance/snapshots?employee_id=${eid}&from_date=${mtd.period_from}&to_date=${mtd.period_to}&attendance_status=Present`} />
                <KpiTile testid="kpi-absent-days" label="Absent" value={mtd.absent_days || 0} tone="rose" icon={<XCircle size={18} />}
                    to={`/admin/attendance/snapshots?employee_id=${eid}&from_date=${mtd.period_from}&to_date=${mtd.period_to}&attendance_status=Absent`} />
                <KpiTile testid="kpi-half-days" label="Half Days" value={mtd.half_days || 0} tone="amber" />
                <KpiTile testid="kpi-leave-days" label="Leave" value={mtd.leave_days || 0} tone="violet" />
                <KpiTile testid="kpi-late-count" label="Late" value={mtd.late_count || 0} tone="amber" icon={<Hourglass size={18} />} />
            </section>

            {/* Last 12 Months Trend strip */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="att-portal-12m-trend">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-sm font-semibold text-slate-800">My Last 12 Months Attendance Trend</h3>
                        <p className="text-xs text-slate-500">Monthly attendance % · Bars colour-coded by heat-map band</p>
                    </div>
                </div>
                <div className="flex items-end gap-2 h-32" data-testid="att-portal-12m-bars">
                    {months.map((m) => (
                        <div key={m.period} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`12m-bar-${m.period}`}>
                            <div className="text-[9px] text-slate-500 font-mono opacity-0 group-hover:opacity-100 transition">{m.attendance_pct.toFixed(0)}%</div>
                            <div className="w-full bg-slate-100 rounded-t flex items-end" style={{ height: "100%" }}>
                                <div className={`w-full rounded-t ${pctBand(m.attendance_pct)} transition-all`}
                                     style={{ height: `${(m.attendance_pct / maxBarPct) * 100}%` }}
                                     title={`${m.period}: ${m.attendance_pct.toFixed(1)}% (${m.reliability_classification})`} />
                            </div>
                            <div className="text-[9px] text-slate-600 font-mono">{m.period.slice(5)}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-3 flex gap-4 text-[10px] text-slate-500 justify-center">
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded-sm" />≥95%</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500 rounded-sm" />85-95%</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-rose-500 rounded-sm" />&lt;85%</span>
                </div>
            </section>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Pending Regularizations */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-portal-pending-regs">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <FileText size={14} /> Pending Regularizations ({data.pending_regularizations_count})
                        </h3>
                        <Link to="/regularization/my" className="text-xs text-slate-500 hover:underline" data-testid="att-portal-all-regs-link">View all</Link>
                    </div>
                    {data.pending_regularizations.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">No pending regularizations.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.pending_regularizations.slice(0, 5).map((r) => (
                                <li key={r.id} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => window.location.assign(`/regularization/my`)} data-testid={`portal-reg-${r.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-slate-800">{r.request_type.replaceAll("_", " ")}</div>
                                        <Badge className="bg-amber-100 text-amber-800 text-xs">{r.status}</Badge>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">{formatDate(r.attendance_date)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Recent Anomalies */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-portal-anomalies">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Warning size={14} /> Recent Anomalies ({data.recent_anomalies_count})
                        </h3>
                        <Link to="/attendance/anomalies?scope=mine" className="text-xs text-slate-500 hover:underline" data-testid="att-portal-anomalies-link">View all</Link>
                    </div>
                    {data.recent_anomalies.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">No anomalies. Keep it up!</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.recent_anomalies.slice(0, 5).map((a) => (
                                <li key={a.id} className="px-5 py-3 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/attendance/anomalies?scope=mine`)}
                                    data-testid={`portal-anomaly-${a.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-slate-800">{a.anomaly_type.replaceAll("_", " ")}</div>
                                        <Badge className={`text-xs ${a.severity === "Critical" ? "bg-rose-100 text-rose-800" : a.severity === "High" ? "bg-orange-100 text-orange-800" : "bg-amber-100 text-amber-800"}`}>{a.severity}</Badge>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">{formatDate(a.attendance_date)} · {a.status}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}
